import React, { useState, useEffect } from 'react'
import { Card, Heading } from '@radix-ui/themes'
import DiscoLogo from '@disco/accounts/components/ui/logo'
import DynamicForm from './DynamicForm'
import SuccessStep from './SuccessStep'
import { cn } from '@disco/accounts/utils/utils'
import { handleSourceUrlBlur, useSignupTypes, SignupStep } from './signup-data'
import { signup, generateLogo } from '@disco/accounts/utils/api'
import mixpanel from '@disco/api/mixpanel'
import {
  readReferral,
  readReferralLanding,
  readSaveToDisco,
} from '@disco/accounts/utils/cookie-utils'
import slugo from '@disco/accounts/utils/slugger'
import { useTranslation } from '@disco/i18n/use-translation'
import Recaptcha from './recaptcha'

export interface SignupData extends Record<string, unknown> {
  email?: string
  fullName?: string
  business_name?: string
  subdomain?: string
  userType?: string
  user_role?: string
  verification_source?: string
  verification_handle?: string
  verification_source_1?: string
  verification_handle_1?: string
  verification_source_2?: string
  verification_handle_2?: string
  marketing_optin?: boolean
  logo?: File | null
  businessType?: string
  teamSize?: string
  catalogSize?: string
  tags?: string[]
  termsAcceptance?: boolean
  partner_source?: string
}

export interface SignupFormProps {
  onSuccess?: (data: SignupData) => void
  onError?: (error: string) => void
}

export default function SignupForm({ onSuccess, onError }: SignupFormProps) {
  const { t } = useTranslation('accounts')
  const SIGNUP_TYPES = useSignupTypes()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [formErrorFallback, setFormErrorFallback] = useState<string | null>(
    null,
  ) // TODO: remove this fallback

  const [currentSteps, setCurrentSteps] = useState<SignupStep[]>(
    // @ts-expect-error ts(2532)
    SIGNUP_TYPES[0].steps,
  )
  const [signupData, setSignupData] = useState<SignupData>({})

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const partnerSource = params.get('partner_source')
    if (partnerSource) {
      setSignupData(prev => ({ ...prev, partner_source: partnerSource }))
    }
  }, [])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [selectedTypeKey, setSelectedTypeKey] = useState<string | null>(null)

  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaTokenRequests, setCaptchaTokenRequests] = useState(0)

  useEffect(() => {
    const captchaRefresh = setInterval(() => {
      setCaptchaTokenRequests(prev => prev + 1)
    }, 110000) // Refresh every 110 seconds as captcha token is valid for 120 seconds
    return () => clearInterval(captchaRefresh)
  }, [])

  const handleStepSubmit = (stepData: Partial<SignupData>) => {
    const newData = { ...signupData, ...stepData }
    setSignupData(newData)

    // GA event to track step submission
    window.gtag?.('event', 'signup_step_submit', {
      page: '/signup',
      context: 'signup_form',
      step: currentStepIndex,
      step_title: getCurrentStep()?.title || 'Unknown Step',
    })

    // Step 0 -> Step 1
    if (currentStepIndex === 0) {
      // @ts-expect-error ts(2532)
      setCurrentSteps([SIGNUP_TYPES[0].steps[0], SIGNUP_TYPES[1].steps[1]])
      setCurrentStepIndex(1)
      return
    }

    // Step 1: pick user type and expand steps
    if (currentStepIndex === 1) {
      if (!stepData.userType) {
        setSelectedTypeKey(null)
        // @ts-expect-error ts(2532)
        setCurrentSteps([SIGNUP_TYPES[0].steps[0], SIGNUP_TYPES[1].steps[1]])
        return
      }

      if (stepData.userType === 'business' && !newData.verification_source) {
        setSignupData({ ...newData, verification_source: 'website_url' })
      }

      if (stepData.userType === 'artist') {
        setSignupData({
          ...newData,
          verification_source_1:
            newData.verification_source_1 ?? 'instagram_handle',
          verification_source_2: newData.verification_source_2 ?? 'spotify_url',
        })
      }

      const selectedType = SIGNUP_TYPES.find(
        t => t.user.key === stepData.userType,
      )
      if (selectedType) {
        const isTypeChange =
          selectedTypeKey !== null && selectedTypeKey !== stepData.userType
        setSelectedTypeKey(stepData.userType as string)

        // @ts-expect-error ts(2532)
        const baseSteps = [SIGNUP_TYPES[0].steps[0], SIGNUP_TYPES[1].steps[1]]
        const newSteps = [...baseSteps, ...selectedType.steps]
        // @ts-expect-error ts(2532)
        setCurrentSteps(newSteps)

        if (isTypeChange) {
          setCurrentStepIndex(2)
          return
        }

        const isLastStep = currentStepIndex === newSteps.length - 1
        if (isLastStep) {
          handleFinalSubmit(newData)
          return
        } else {
          setCurrentStepIndex(currentStepIndex + 1)
          return
        }
      }
    }

    // Other steps
    const isLastStep = currentStepIndex === currentSteps.length - 1
    if (isLastStep) {
      handleFinalSubmit(newData)
    } else {
      setCurrentStepIndex(currentStepIndex + 1)
    }
  }

  const handleBack = () => {
    if (currentStepIndex === 0) return
    setCurrentStepIndex(currentStepIndex - 1)
  }

  const handleFinalSubmit = async (data: SignupData) => {
    setIsSubmitting(true)
    setFormErrorFallback(null)
    // Refresh captcha token after submission to ensure it's available for next request
    setCaptchaTokenRequests(prev => prev + 1)
    try {
      const nameParts = (data.fullName as string)?.split(' ') || []
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      let logo = data.logo
      if (!logo && data.business_name) {
        try {
          const logoBlob = await generateLogo(data.business_name as string)
          logo = new File([logoBlob], `${data.business_name}-logo.png`, {
            type: 'image/png',
          })
        } catch (err) {
          console.error('Failed to generate logo:', err)
        }
      }

      const apiData: Record<string, unknown> = {
        email: data.email,
        // i18next-instrument-ignore
        name: `${firstName} ${lastName}`,
        business_name: data.business_name,
        subdomain: data.subdomain,
        business_type:
          data.userType === 'artist' ? 'music_creator' : data.businessType,
        user_role: data.user_role,
        marketing_optin: data.marketing_optin,
        company_size: data.teamSize,
        catalog_size: data.catalogSize,
        terms_acceptance: data.termsAcceptance,
        partner_source: data.partner_source,
        ...(logo && { logo }),
        ...(data.tags && { tags: data.tags }),
        captcha_token: captchaToken,
      }

      if (data.verification_source) {
        data.verification_handle = handleSourceUrlBlur(
          data.verification_source,
          data.verification_handle,
        )
        apiData[data.verification_source] = data.verification_handle
      }

      if (data.verification_source_1) {
        data.verification_handle_1 = handleSourceUrlBlur(
          data.verification_source_1,
          data.verification_handle_1,
        )
        apiData[data.verification_source_1] = data.verification_handle_1
      }

      if (data.verification_source_2) {
        data.verification_handle_2 = handleSourceUrlBlur(
          data.verification_source_2,
          data.verification_handle_2,
        )
        apiData[data.verification_source_2] = data.verification_handle_2
      }

      const result = await signup(apiData)
      console.log('API response:', result)

      setIsSuccess(true)
      onSuccess?.(data)

      const finalNameParts = (data.fullName as string)?.split(' ') || []
      const finalFirstName = finalNameParts[0]
      const finalLastName = finalNameParts.slice(1).join(' ')

      const referral = readReferral()
      const saveToDisco = readSaveToDisco()

      const subdomainPlaceholder = data.business_name
        ? slugo(data.business_name)
        : ''

      const mixpanelData = {
        business_name: data.business_name,
        email: data.email,
        // i18next-instrument-ignore
        name: `${firstName} ${lastName}`,
        subdomain: data?.subdomain?.trim?.().length
          ? data.subdomain.trim().replace(/(^https?:\/\/)|(\.disco\.ac)/gi, '')
          : subdomainPlaceholder || undefined,
        save_to_disco_object_url: saveToDisco.savingUrl,
        save_to_disco_files_selection_id: saveToDisco.savingObj,
        tags: data.selectedTags,
        logo: data.file ? data.file : undefined,
        referral: referral.legacyStringRef || undefined,
        referring_user: referral.userId || undefined,
        referring_business: referral.businessId || undefined,
        landing: readReferralLanding() || undefined, // landing null or '' isn't allowed
        terms_acceptance: data.termsAcceptance,
      }

      mixpanel.sendPureEvent('Referral signup trials created', {
        regId: result.id,
        data: mixpanelData,
      })

      const signupType = data.userType || 'business'

      window.gtag?.('event', 'signup_form_completed', {
        page_path: window.location.pathname,
        context: 'signup_form',
      })
    } catch (error) {
      console.error('Signup error:', error)
      try {
        const apiError = error as Error & { response: { string: string[] } }
        const text = Object.entries(apiError.response)
          .flatMap(([k, vs]) =>
            (vs as unknown as string[]).map(
              // i18next-instrument-ignore
              v => `${k.replace(/_/g, ' ')} - ${v}`,
            ),
          )
          .join('\n\n')
        console.error('Signup error details', text)
        setFormErrorFallback(text)
      } catch (error2) {
        console.error('Signup error while formatting:', error2)
      }
      onError?.(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getCurrentStep = (): SignupStep | null => {
    return currentSteps[currentStepIndex] || null
  }

  const renderStepBadges = () => {
    if (currentStepIndex === 0) return null

    const totalBadges = 4
    const currentStepInStepper = currentStepIndex - 1

    return (
      <div className="flex gap-1 mb-[24px]">
        {Array.from({ length: totalBadges }, (_, i) => (
          <span
            key={i}
            className={cn(
              'flex-1 h-1 rounded-full',
              i <= currentStepInStepper ? 'bg-black' : 'bg-[#c1c9d2]',
            )}
          />
        ))}
      </div>
    )
  }

  const getMainTitle = () => {
    if (currentStepIndex === 0)
      return t(
        'accounts:startYourFreeDiscoTrial',
        'Start your free DISCO trial.',
      )
    if (currentStepIndex === 1)
      return t('accounts:createYourAccount', 'Create your account')

    const isLastStep = currentStepIndex === currentSteps.length - 1
    if (isLastStep) return t('accounts:oneLastStep', 'One last step!')

    if (currentStepIndex >= 2) {
      const fullName = signupData.fullName as string
      const firstName = fullName?.split(' ')[0] || ''
      return firstName
        ? t(
            'accounts:welcomeToDiscoFirstname',
            'Welcome to DISCO, {{firstName}}!',
            {
              firstName,
            },
          )
        : t('accounts:welcomeToDisco', 'Welcome to DISCO!')
    }

    return t('accounts:startYourFreeDiscoTrial', 'Start your free DISCO trial.')
  }

  const getStepTitle = () => {
    if (currentStepIndex === 0)
      return t('accounts:letsStartWithYourEmail', 'Let’s start with your email')
    if (currentStepIndex === 1)
      return t('accounts:tellUsABitAboutYou', 'Tell us a bit about you')
    return getCurrentStep()?.title || ''
  }

  if (isSuccess) {
    return <SuccessStep data={signupData} />
  }

  const currentStep = getCurrentStep()
  if (!currentStep) {
    return null
  }

  // i18next-instrument-ignore
  const mainTitleClassName =
    'text-[48px] sm:text-[64px] lg:text-[96px] font-plakat font-bold leading-[48px] sm:leading-[64px] lg:leading-[96px] text-center uppercase m-0'

  return (
    <div className="min-h-screen bg-white relative flex flex-col items-center justify-center p-4">
      <div className="absolute top-[52px] left-[57px]">
        <DiscoLogo className="text-black" />
      </div>

      {/* Main title outside the white card */}
      <div className="flex flex-col gap-[8px] leading-[0] text-center text-black mb-[var(--Spacing-8,48px)]">
        <div className="flex flex-col justify-end w-full">
          {currentStepIndex === 0 ? (
            <h1 className={mainTitleClassName}>{getMainTitle()}</h1>
          ) : (
            <Heading as="h1" size="8" weight="bold" className="m-0">
              {getMainTitle()}
            </Heading>
          )}
        </div>
        {currentStepIndex === 0 && (
          <div className="flex flex-col justify-end w-full max-sm:hidden">
            <p className="text-[35px] font-bold leading-[40px] tracking-[-0.16px] m-0">
              {t(
                'accounts:uploadOrganizeShareReceiveFilesWithEase',
                'Upload, organize, share & receive files with ease.',
              )}
            </p>
          </div>
        )}
      </div>

      {/* White card with form */}
      <Card
        className="max-w-[500px] w-full mx-auto relative [--accent-9:#18181b] [--accent-contrast:#f9f9f9]"
        size="3"
      >
        <Heading
          as="h2"
          size="5"
          weight="medium"
          align={currentStepIndex === 0 ? 'center' : 'left'}
          mb={currentStepIndex === 0 ? '3' : '4'}
          className="font-neue tracking-[-0.08px]"
        >
          {getStepTitle()}
        </Heading>

        {renderStepBadges()}

        <DynamicForm
          step={currentStep}
          onNext={handleStepSubmit}
          onBack={currentStepIndex > 0 ? handleBack : undefined}
          data={signupData}
          isLoading={isSubmitting || !captchaToken}
          isSubmitting={isSubmitting}
          stepIndex={currentStepIndex}
          isLastStep={currentStepIndex === currentSteps.length - 1}
        />
        <div className="text-sm text-red-600 whitespace-pre-wrap">
          {formErrorFallback}
        </div>
        <Recaptcha
          type="signup"
          onTokenReceived={setCaptchaToken}
          tokenRequests={captchaTokenRequests}
        />
      </Card>
    </div>
  )
}
